import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { Card, Group, Select, Table, Text, TextInput, Textarea, Button } from '@mantine/core';
import type { IncidentDetail, IncidentOptions } from '@isg/shared-types';
import { useOlayEylem, useOlaySatirSil } from '../api/queries';
import { OLAY_ARASTIRMACI_ROL_ETIKET } from '../i18n/tr';
import { zamanGoster } from '../lib/format';
import { SatirEylemleri, SolukSatir } from '../sozlesme';
import { ANLAM } from '../theme';

/**
 * ARAŞTIRMA ALANLARI (F4·B2·2 — madde 146): ortam · kronoloji · ekip.
 *
 * ⚠️ **GÖRÜNÜRLÜK SUNUCUDAN** (§2.40): yazma denetimleri YALNIZ
 * `yetkiler.arastirmaKaydiYazabilir` bayrağıyla çizilir — sunucudaki içerik
 * kilidi (`olayAcikMi`) aynı yüklemdir. Yazamayan kullanıcı bölümleri SALT
 * OKUNUR görür (gizlenmez — araştırmanın neyi soracağı önceden de
 * görülebilmeli; B2·1'in 138 dersi).
 *
 * ⚠️ **HİÇBİR ALAN ZORUNLU DEĞİL** (O6 — kapanış kapısı sıkılaştırılmaz).
 */

// ── Ortam bilgileri ──────────────────────────────────────────────────────────

export function OrtamBolumu({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  const eylem = useOlayEylem();
  const yazabilir = olay.yetkiler.arastirmaKaydiYazabilir;
  const [saat, setSaat] = useState(olay.ortam.olaySaati ?? '');
  const [hava, setHava] = useState(olay.ortam.havaDurumu ?? '');
  const [aydinlatma, setAydinlatma] = useState<string | null>(olay.ortam.aydinlatmaRefId);
  const [zemin, setZemin] = useState<string | null>(olay.ortam.zeminRefId);
  const [vardiya, setVardiya] = useState(olay.ortam.vardiya ?? '');

  const saatHatasi = saat.trim() !== '' && !/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(saat.trim());

  if (!yazabilir) {
    // Salt okunur görünüm — kontrol çalışamayacaksa görünmez ya da salt
    // okunur olur (§2.40 sıralaması); veri okuyucudan saklanmaz.
    return (
      <Card withBorder data-testid="olay-ortam">
        <Text fw={600}>Ortam bilgileri</Text>
        <Group gap="lg" wrap="wrap" mt="sm">
          <OrtamDegeri etiket="Saat" deger={olay.ortam.olaySaati} />
          <OrtamDegeri etiket="Hava durumu" deger={olay.ortam.havaDurumu} />
          <OrtamDegeri etiket="Aydınlatma" deger={olay.ortam.aydinlatmaAd} />
          <OrtamDegeri etiket="Zemin" deger={olay.ortam.zeminAd} />
          <OrtamDegeri etiket="Vardiya" deger={olay.ortam.vardiya} />
        </Group>
      </Card>
    );
  }

  return (
    <Card withBorder data-testid="olay-ortam">
      <Group justify="space-between">
        <Text fw={600}>Ortam bilgileri</Text>
        <Button
          size="xs"
          variant="light"
          data-testid="olay-ortam-kaydet"
          loading={eylem.isPending}
          disabled={saatHatasi}
          onClick={() =>
            eylem.mutate({
              id: olay.id,
              eylem: 'ortam',
              govde: {
                olaySaati: saat.trim() === '' ? null : saat.trim(),
                havaDurumu: hava.trim() === '' ? null : hava.trim(),
                aydinlatmaRefId: aydinlatma,
                zeminRefId: zemin,
                vardiya: vardiya.trim() === '' ? null : vardiya.trim(),
              },
            })
          }
        >
          Ortamı kaydet
        </Button>
      </Group>
      <Text size="xs" c="dimmed">
        Hiçbir alan zorunlu değildir — bilinenler yazılır.
      </Text>
      <Group gap="sm" mt="sm" wrap="wrap" align="flex-start">
        <TextInput
          label="Saat"
          placeholder="14:30"
          w={110}
          value={saat}
          error={saatHatasi ? 'SS:DD biçiminde yazın (ör. 14:30).' : null}
          onChange={(event) => setSaat(event.currentTarget.value)}
          data-testid="olay-ortam-saat"
        />
        <TextInput
          label="Hava durumu"
          w={190}
          value={hava}
          onChange={(event) => setHava(event.currentTarget.value)}
          data-testid="olay-ortam-hava"
        />
        <Select
          label="Aydınlatma"
          w={210}
          clearable
          data={options.aydinlatmalar.map((k) => ({ value: k.id, label: k.ad }))}
          value={aydinlatma}
          onChange={setAydinlatma}
          data-testid="olay-ortam-aydinlatma"
        />
        <Select
          label="Zemin"
          w={190}
          clearable
          data={options.zeminler.map((k) => ({ value: k.id, label: k.ad }))}
          value={zemin}
          onChange={setZemin}
          data-testid="olay-ortam-zemin"
        />
        <TextInput
          label="Vardiya"
          w={150}
          value={vardiya}
          onChange={(event) => setVardiya(event.currentTarget.value)}
          data-testid="olay-ortam-vardiya"
        />
      </Group>
      {eylem.error && (
        <Text c={ANLAM.engel} size="sm" role="alert" data-testid="olay-ortam-hata" mt="sm">
          {eylem.error.message}
        </Text>
      )}
    </Card>
  );
}

function OrtamDegeri({
  etiket,
  deger,
}: {
  etiket: string;
  deger: string | null;
}): React.JSX.Element {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {etiket}
      </Text>
      <Text size="sm">{deger ?? '—'}</Text>
    </div>
  );
}

// ── Kronoloji (A2 — TEK TAŞIYICI) ────────────────────────────────────────────

export function KronolojiBolumu({ olay }: { olay: IncidentDetail }): React.JSX.Element {
  const eylem = useOlayEylem();
  const sil = useOlaySatirSil();
  const yazabilir = olay.yetkiler.arastirmaKaydiYazabilir;
  const [zaman, setZaman] = useState('');
  const [metin, setMetin] = useState('');

  // datetime-local girdisi ya boş ya tarayıcının doğruladığı bir değerdir;
  // elle yazılabilen tarayıcılar için NaN denetimi yine de burada (§2.40 —
  // kontrol ya çalışır ya kapalıdır).
  const zamanGecersiz = zaman.trim() !== '' && Number.isNaN(new Date(zaman).getTime());
  const dokunuldu = zaman.trim() !== '' || metin.trim() !== '';
  // P12 (madde 224) — kaydedilmemiş kronoloji satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'kronoloji satırı');
  const kaydedilebilir = metin.trim() !== '' && !zamanGecersiz;

  return (
    <Card withBorder data-testid="olay-kronoloji">
      <Text fw={600}>Kronoloji</Text>
      <Text size="xs" c="dimmed">
        Olay öncesi, anı ve sonrası tek zaman çizelgesinde — satırlar sırayla dizilir.
      </Text>
      <Table mt="sm">
        <Table.Tbody>
          {olay.kronoloji.map((satir) => (
            <Table.Tr key={satir.id} data-testid="olay-kronoloji-satiri">
              <Table.Td w={40}>
                <Text size="sm" c="dimmed">
                  {satir.sira}
                </Text>
              </Table.Td>
              <Table.Td w={140}>
                <Text size="sm">{satir.zaman ? zamanGoster(satir.zaman) : '—'}</Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{satir.metin}</Text>
              </Table.Td>
              <Table.Td w={60}>
                {yazabilir && (
                  <SatirEylemleri
                    testId="olay-kronoloji-eylemler"
                    digerleri={[
                      {
                        etiket: 'Sil',
                        tehlikeli: true,
                        testId: 'olay-kronoloji-sil',
                        onClick: () =>
                          sil.mutate({ id: olay.id, liste: 'kronoloji', satirId: satir.id }),
                      },
                    ]}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
          {yazabilir && (
            <SolukSatir
              testId="olay-kronoloji-soluk-satir"
              dokunuldu={dokunuldu}
              kaydedilebilir={kaydedilebilir}
              kaydediliyor={eylem.isPending}
              kolonSayisi={3}
              onKaydet={() => {
                if (!kaydedilebilir) return;
                eylem.mutate(
                  {
                    id: olay.id,
                    eylem: 'kronoloji',
                    govde: {
                      zaman: zaman.trim() === '' ? null : new Date(zaman).toISOString(),
                      metin: metin.trim(),
                    },
                  },
                  {
                    onSuccess: () => {
                      setZaman('');
                      setMetin('');
                    },
                  },
                );
              }}
            >
              <Table.Td w={40}>
                <Text size="sm" c="dimmed">
                  +
                </Text>
              </Table.Td>
              <Table.Td w={220}>
                <TextInput
                  type="datetime-local"
                  size="xs"
                  value={zaman}
                  error={zamanGecersiz ? 'Geçerli bir zaman seçin.' : null}
                  onChange={(event) => setZaman(event.currentTarget.value)}
                  data-testid="olay-kronoloji-zaman"
                />
              </Table.Td>
              <Table.Td>
                <Textarea
                  size="xs"
                  autosize
                  minRows={1}
                  placeholder="Ne oldu?"
                  value={metin}
                  onChange={(event) => setMetin(event.currentTarget.value)}
                  data-testid="olay-kronoloji-metin"
                />
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
      {!yazabilir && olay.kronoloji.length === 0 && (
        <Text size="sm" c="dimmed" data-testid="olay-kronoloji-yok">
          Kronoloji girilmemiş.
        </Text>
      )}
      {(eylem.error || sil.error) && (
        <Text c={ANLAM.engel} size="sm" role="alert" data-testid="olay-kronoloji-hata">
          {eylem.error?.message ?? sil.error?.message}
        </Text>
      )}
    </Card>
  );
}

// ── Araştırma ekibi ──────────────────────────────────────────────────────────

/** ⚠️ EKİP ÜYELİĞİ YETKİ VERMEZ (R6 emsali) — bu bölüm raporun "kim
 *  araştırdı" satırını doldurur; kimseye ekran/uç açmaz. */
export function EkipBolumu({
  olay,
  options,
}: {
  olay: IncidentDetail;
  options: IncidentOptions;
}): React.JSX.Element {
  const eylem = useOlayEylem();
  const sil = useOlaySatirSil();
  const yazabilir = olay.yetkiler.arastirmaKaydiYazabilir;
  const [personId, setPersonId] = useState<string | null>(null);
  const [rol, setRol] = useState<string>('uye');

  return (
    <Card withBorder data-testid="olay-ekip">
      <Text fw={600}>Araştırma ekibi</Text>
      <Text size="xs" c="dimmed">
        Ekip üyeliği bir kayıt bilgisidir — kimseye yetki vermez.
      </Text>
      <Table mt="sm">
        <Table.Tbody>
          {olay.arastirmacilar.map((uye) => (
            <Table.Tr key={uye.id} data-testid="olay-ekip-satiri">
              <Table.Td>
                <Text size="sm">{uye.adSoyad}</Text>
              </Table.Td>
              <Table.Td w={190}>
                <Text size="sm" c="dimmed">
                  {OLAY_ARASTIRMACI_ROL_ETIKET[uye.rol] ?? uye.rol}
                </Text>
              </Table.Td>
              <Table.Td w={60}>
                {yazabilir && (
                  <SatirEylemleri
                    testId="olay-ekip-eylemler"
                    digerleri={[
                      {
                        etiket: 'Çıkar',
                        tehlikeli: true,
                        testId: 'olay-ekip-sil',
                        onClick: () =>
                          sil.mutate({ id: olay.id, liste: 'investigators', satirId: uye.id }),
                      },
                    ]}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
          {yazabilir && (
            <SolukSatir
              testId="olay-ekip-soluk-satir"
              dokunuldu={personId !== null}
              kaydedilebilir={personId !== null}
              kaydediliyor={eylem.isPending}
              kolonSayisi={2}
              onKaydet={() => {
                if (!personId) return;
                eylem.mutate(
                  { id: olay.id, eylem: 'investigators', govde: { personId, rol } },
                  { onSuccess: () => setPersonId(null) },
                );
              }}
            >
              <Table.Td>
                <Select
                  size="xs"
                  searchable
                  placeholder="Kişi seç"
                  data={options.personeller.map((p) => ({ value: p.id, label: p.adSoyad }))}
                  value={personId}
                  onChange={setPersonId}
                  data-testid="olay-ekip-kisi"
                />
              </Table.Td>
              <Table.Td w={190}>
                <Select
                  size="xs"
                  allowDeselect={false}
                  data={Object.entries(OLAY_ARASTIRMACI_ROL_ETIKET).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                  value={rol}
                  onChange={(deger) => deger && setRol(deger)}
                  data-testid="olay-ekip-rol"
                />
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
      {!yazabilir && olay.arastirmacilar.length === 0 && (
        <Text size="sm" c="dimmed" data-testid="olay-ekip-yok">
          Araştırma ekibi atanmamış.
        </Text>
      )}
      {(eylem.error || sil.error) && (
        <Text c={ANLAM.engel} size="sm" role="alert" data-testid="olay-ekip-hata">
          {eylem.error?.message ?? sil.error?.message}
        </Text>
      )}
    </Card>
  );
}
